import {
	AfterViewInit,
	Component,

	HostListener,
	Injectable,
	Input,
	OnDestroy,
	OnInit,
	Optional,

	ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { VisualNotificationComponent } from '@app/workflow/components/questionnaire/questionnaire-item/special-question/optische-reaktion/visual-notification.component';
import { cast, IQuestionnaireGroup } from '@interfaces/interfaces';
import { BlockQuestionItem, RangeQuestion, SpecialQuestionItem, SpecialQuestionType } from '@interfaces/questions';
import { Subscription, timer } from 'rxjs';
import { WizardPage } from '@app/workflow/components/navigator/wizard-view/interfaces';
import { WorkflowItemKeyDirective } from '@workflow/directives/workflow-item-key/workflow-item-key.directive';
import {
	AbstractSpecialQuestionComponent,
	ISpecialQuestionComponent
} from '../special-question.service';
import { AbstractReaktion } from '@workflow/components/questionnaire/questionnaire-item/special-question/abstract-reaction';
import { OptischeReaktionEpilogComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/optische-reaktion/optische-reaktion-epilog.component';

@Injectable()
export class OptischeReaktion extends AbstractReaktion {
	readonly COUNT = 10;
	public readonly prologueFullHeight = true;
	public readonly component = OptischeReaktionComponent;
	public readonly specialQuestionType: SpecialQuestionType = 'OptischeReaktion';
	public readonly requiredDevices = [];

	title = 'Optische Reaktion';
	missingValueKeyGroup = 'fehlwert';

	public readonly defaultGroupConfiguration: IQuestionnaireGroup = {
		title: this.title,
		preface: ['<div class="d-flex flex-column justify-content-between align-items-centered height-100p"><div class="max-height-50p">Zur Bestimmung der optischen Reaktion erscheint im folgenden Test' +
		' 10-mal eine rote Kreisfläche in unterschiedlichen Zeitabständen. Sobald die Kreisfläche erscheint, "löschen" Sie diese so schnell wie möglich mit ' +
		'dem Buzzer. Sie beginnen den Test durch Betätigen der “Test Starten” Schaltfläche auf der nächsten Seite.</div>' +
		'<div class="max-height-50p">' +
		'<img class="object-fit-contain" src="assets/images/descriptions/RedCircle.png" alt="redc circle"/></div></div>'],
		items: [
			cast<BlockQuestionItem>({
				type: 'BlockQuestion',
				title: 'Optische Reaktion - Messwerte I - V',
				items: [
					{
						label: 'Messung 1',
						key: '0',
					}, {
						label: 'Messung 2',
						key: '1',
					}, {
						label: 'Messung 3',
						key: '2',
					}, {
						label: 'Messung 4',
						key: '3',
					}, {
						label: 'Messung 5',
						key: '4',
					},
				],
				default: cast<RangeQuestion>({
					type: 'range',
					rangeLow: 0,
					rangeHigh: 1000,
					steps: 5,
					unit: 'msek',
				}),
				isProcedure: true,
			}), cast<BlockQuestionItem>({
				type: 'BlockQuestion',
				title: 'Optische Reaktion - Messwerte VI - X',
				items: [
					{
						label: 'Messung 6',
						key: '5',
					}, {
						label: 'Messung 7',
						key: '6',
					}, {
						label: 'Messung 8',
						key: '7',
					}, {
						label: 'Messung 9',
						key: '8',
					}, {
						label: 'Messung 10',
						key: '9',
					},
				],
				default: cast<RangeQuestion>({
					type: 'range',
					rangeLow: 0,
					rangeHigh: 1000,
					steps: 5,
					unit: 'msek',
				}),
				isProcedure: true,
			}),
		],
		startText: 'actions.goToTest',
	};

	buildWizardPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		return [{
			...group,
			question: specialQuestion,
			subpage: 1,
			obligatoryUnskippableKeys: [specialQuestion.key],
			obligatoryAllKindOfKeys: [specialQuestion.key],
			disableNext: true,
		}];
	}

	buildEpilogPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		return [{
			...group,
			question: {
				type: 'epilog',
				title: group.title,
				centered: true,
			},
			obligatoryUnskippableKeys: [],
			obligatoryAllKindOfKeys: [],
			disableNext: false,
			isProcedure: true,
			alignBottom: true,
			epilogComponent: OptischeReaktionEpilogComponent,
			key: specialQuestion.key,
		}];
	}
}

@Component({
	selector: 'workflow-optische-reaktion',
	templateUrl: './optische-reaktion.component.html',
	styleUrls: ['./optische-reaktion.component.scss',
		'../../../../../../workflow/components/navigator/wizard-view/wizard-view.component.scss']
})
export class OptischeReaktionComponent extends AbstractSpecialQuestionComponent implements OnInit, OnDestroy, AfterViewInit, ISpecialQuestionComponent {

	public static count = 10;

	results: number[] = [];
	timerMin = 1500;
	timerMax = 3500;

	public testRunning: boolean;
	private subscription = new Subscription();

	public buttonText = 'actions.starttest';

	@Input() page: number;

	@ViewChild(VisualNotificationComponent)
	notification: VisualNotificationComponent;

	constructor(@Optional() private workflowItemKey: WorkflowItemKeyDirective, router: Router) {
		super(router);
	}

	async dispatchVitaReact($event: Event): Promise<void> {
		window.dispatchEvent(new Event('vitareact-press'));
		window.dispatchEvent(new Event('vitareact-release'));
		$event.preventDefault();
	}

	@HostListener('window:keydown', ['$event'])
	keyPress($event: Event): void {
		window.dispatchEvent(new Event('vitareact-press'));
		window.dispatchEvent(new Event('vitareact-release'));
		$event.preventDefault();
	}

	@HostListener('window:vitareact-press', ['$event'])
	vitaReactPress($event: Event): void {
		this.hitButton($event.timeStamp);
	}

	ngOnInit(): void {
		this.subscription.add(this.workflowItemKey.propertyFull$.subscribe(data => {
			if (this.finishedAndLocked) {
				this.results = data && typeof(data[0]) === 'number' ? data as number[] : [];
				this.goToEpilog();
			}
		}));
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	ngAfterViewInit(): void {
		this.results = [];
	}

	private get completed(): boolean {
		return this.results?.length >= OptischeReaktionComponent.count;
	}

	private checkProgressAndMove(): void {
		const completed = this.completed;
		this.valid.emit(completed);

		if (completed) {
			this.updateResults();
			this.goToEpilog();
			return;
		} else {
			this.startTest().catch(console.error);
		}
	}

	private updateResults(): void {
		this.workflowItemKey.setProperty({...this.results});
	}

	private async startTest(): Promise<void> {
		if (this.testRunning) {
			return;
		}
		this.buttonText = 'actions.stop';
		this.testRunning = true;

		this.notification.active = false;

		const initialDelay = Math.floor(Math.random() * (this.timerMax - this.timerMin + 1)) + this.timerMin;

		await timer(initialDelay).toPromise();

		this.notification.active = true;
	}

	private stopTest(): void {
		this.notification.deactivate();
		this.testRunning = false;
	}

	hitButton(eventTimestamp: number): void {
		if (!this.finishedAndLocked) {
			if (!this.testRunning) {
				this.startTest();
			} else if (this.notification.active) {
				const duration = Math.floor(this.notification.duration(eventTimestamp));
				if (duration < 0) {
					return;
				}
				this.stopTest();
				if (!this.completed) {
					this.results.push(duration);
				}
			}
			this.checkProgressAndMove();
		}
	}

}
